import React from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import { ToastContainer, PageLoader } from './components/UI'
import { LoginPage, RegisterPage } from './pages/Auth'
import HomePage from './pages/Home'
import DashboardPage from './pages/Dashboard'
import ExplorePage from './pages/Explore'
import SpaceDetailPage from './pages/SpaceDetail'
import { InventoryPage, NewSpacePage } from './pages/Inventory'
import { BookingsPage, BookingDetailPage } from './pages/Bookings'
import { SettingsPage, NotificationsPage } from './pages/Settings'
import './index.css'

// ── Route guards ─────────────────────────────────────────────
function PrivateRoute({ children }) {
  const { user, loading } = useAuth()
  if (loading) return <PageLoader />
  if (!user) return <Navigate to="/login" replace />
  return children
}

function OwnerRoute({ children }) {
  const { user, loading, isMediaOwner } = useAuth()
  if (loading) return <PageLoader />
  if (!user) return <Navigate to="/login" replace />
  if (!isMediaOwner) return <Navigate to="/dashboard" replace />
  return children
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth()
  if (loading) return <PageLoader />
  if (user) return <Navigate to="/dashboard" replace />
  return children
}

// ── App Router ───────────────────────────────────────────────
function AppRoutes() {
  return (
    <Routes>
      {/* Homepage - visible to everyone */}
      <Route path="/" element={<HomePage />} />

      {/* Public auth routes */}
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />

      {/* Protected routes - all users */}
      <Route path="/dashboard" element={<PrivateRoute><DashboardPage /></PrivateRoute>} />
      <Route path="/explore" element={<PrivateRoute><ExplorePage /></PrivateRoute>} />
      <Route path="/spaces/:id" element={<PrivateRoute><SpaceDetailPage /></PrivateRoute>} />
      <Route path="/bookings" element={<PrivateRoute><BookingsPage /></PrivateRoute>} />
      <Route path="/bookings/:id" element={<PrivateRoute><BookingDetailPage /></PrivateRoute>} />
      <Route path="/notifications" element={<PrivateRoute><NotificationsPage /></PrivateRoute>} />
      <Route path="/settings" element={<PrivateRoute><SettingsPage /></PrivateRoute>} />

      {/* Media owner only */}
      <Route path="/inventory" element={<OwnerRoute><InventoryPage /></OwnerRoute>} />
      <Route path="/inventory/new" element={<OwnerRoute><NewSpacePage /></OwnerRoute>} />

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <ToastContainer />
      </AuthProvider>
    </BrowserRouter>
  )
}
